/**
 * Owner: Masa Bayu
 * File: typography-system.ts
 * Date:Time: 2026-09-13 21:05
 * Description: 22B QXTypographySystem container + cross-scale validation.
 */
import { createQXFontFamily, isQXFontFamily } from './typography-font';
import { createQXTypographyRoleStyle, isQXTypographyRole, } from './typography-role';
import { createQXTypographyLetterSpacingScale, createQXTypographyLineHeightScale, createQXTypographySizeScale, isQXTypographyLetterSpacingScale, isQXTypographyLineHeightScale, isQXTypographySizeScale, } from './typography-scale';
/** Typography subsystem model version (distinct from package / design-system model). */
export const QX_TYPOGRAPHY_SYSTEM_MODEL_VERSION = '1.0.0';
export const QX_TYPOGRAPHY_SYSTEM_FORBIDDEN_KEYS = [
    'react',
    'component',
    'css',
    'className',
    'style',
    'dom',
    'html',
    'fontFace',
    'load',
    'loader',
    'renderer',
    'layout',
    'qxw',
    'qxp',
    'widget',
    'content',
    'text',
];
function assertNoForbidden(record) {
    for (const key of QX_TYPOGRAPHY_SYSTEM_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`Typography system must not include forbidden field "${key}"`);
        }
    }
}
export function createQXTypographySystem(options) {
    assertNoForbidden({ ...options });
    const fonts = options.fonts.map((font) => createQXFontFamily(font));
    const fontIds = new Set();
    for (const font of fonts) {
        if (fontIds.has(font.id)) {
            throw new Error(`Duplicate typography font id: ${font.id}`);
        }
        fontIds.add(font.id);
    }
    const sizeScale = createQXTypographySizeScale(options.sizeScale.sizes);
    const lineHeightScale = createQXTypographyLineHeightScale(options.lineHeightScale.lineHeights);
    const letterSpacingScale = createQXTypographyLetterSpacingScale(options.letterSpacingScale.letterSpacings);
    const sizeSteps = new Set(sizeScale.sizes.map((item) => item.step));
    const lineHeightSteps = new Set(lineHeightScale.lineHeights.map((item) => item.step));
    const letterSpacingSteps = new Set(letterSpacingScale.letterSpacings.map((item) => item.step));
    const roles = options.roles.map((role) => createQXTypographyRoleStyle(role));
    const seenRoles = new Set();
    for (const role of roles) {
        if (seenRoles.has(role.role)) {
            throw new Error(`Duplicate typography role: ${role.role}`);
        }
        seenRoles.add(role.role);
        if (!fontIds.has(role.fontFamilyId)) {
            throw new Error(`Typography role "${role.role}" references unknown fontFamilyId "${role.fontFamilyId}"`);
        }
        if (!sizeSteps.has(role.sizeStep)) {
            throw new Error(`Typography role "${role.role}" references unknown sizeStep "${role.sizeStep}"`);
        }
        if (!lineHeightSteps.has(role.lineHeightStep)) {
            throw new Error(`Typography role "${role.role}" references unknown lineHeightStep "${role.lineHeightStep}"`);
        }
        if (!letterSpacingSteps.has(role.letterSpacingStep)) {
            throw new Error(`Typography role "${role.role}" references unknown letterSpacingStep "${role.letterSpacingStep}"`);
        }
    }
    return Object.freeze({
        modelVersion: QX_TYPOGRAPHY_SYSTEM_MODEL_VERSION,
        fonts: Object.freeze(fonts),
        sizeScale,
        lineHeightScale,
        letterSpacingScale,
        roles: Object.freeze(roles),
    });
}
export function isQXTypographySystem(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return (record.modelVersion === QX_TYPOGRAPHY_SYSTEM_MODEL_VERSION &&
        Array.isArray(record.fonts) &&
        isQXTypographySizeScale(record.sizeScale) &&
        isQXTypographyLineHeightScale(record.lineHeightScale) &&
        isQXTypographyLetterSpacingScale(record.letterSpacingScale) &&
        Array.isArray(record.roles));
}
export function validateQXTypographySystem(value) {
    if (!isQXTypographySystem(value)) {
        throw new Error('Invalid typography system');
    }
    assertNoForbidden({ ...value });
    for (const font of value.fonts) {
        if (!isQXFontFamily(font)) {
            throw new Error('Invalid typography font family');
        }
    }
    for (const role of value.roles) {
        if (!isQXTypographyRole(role.role)) {
            throw new Error(`Invalid typography role: ${String(role.role)}`);
        }
    }
    createQXTypographySystem(value);
}
//# sourceMappingURL=typography-system.js.map